import React from 'react';
import { IDSButton, IDSInput } from '@inera/ids-react';
import {
  isApproved,
  isApprovedWithReservation,
  isRejected,
  isValidReservationTicketKey,
  requiresReservationTicketKey,
  showsReservationTicketKey,
} from './reviewerDecisionQueueUtils';

/**
 * `edge` is the card's left border. It follows the chip text colour except for
 * 'Ingen avvikelse', which is deliberately muted — nothing happens on the AO.
 */
const CHIP_TONES = {
  success: {
    fg: 'var(--ids-palette-success-40)',
    bg: 'var(--ids-palette-success-95)',
    edge: 'var(--ids-palette-success-40)',
  },
  attention: {
    fg: 'var(--ids-palette-attention-40)',
    bg: 'var(--ids-palette-attention-95)',
    edge: 'var(--ids-palette-attention-40)',
  },
  error: {
    fg: 'var(--ids-palette-error-40)',
    bg: 'var(--ids-palette-error-95)',
    edge: 'var(--ids-palette-error-40)',
  },
  information: {
    fg: 'var(--ids-palette-information-40)',
    bg: 'var(--ids-palette-information-95)',
    edge: 'var(--ids-palette-neutral-95)',
  },
  neutral: {
    fg: 'var(--ids-palette-neutral-40)',
    bg: 'var(--ids-palette-neutral-95)',
    edge: 'var(--ids-palette-neutral-90)',
  },
};

const decisionTone = (decision) => {
  const value = String(decision || '').toLowerCase();
  if (value.includes('godkänd med förbehåll')) return 'attention';
  if (value.includes('godkänd')) return 'success';
  if (value.includes('underkänd')) return 'error';
  if (value.includes('ingen avvikelse')) return 'information';
  return 'neutral';
};

const statusChip = (row) => {
  if (row.decisionProcessed) return { label: 'Hanterad', tone: 'success' };
  if (row.decisionProcessingError) return { label: 'Misslyckades', tone: 'error' };
  return { label: 'Ej hanterad', tone: 'neutral' };
};

/**
 * The steps are only worth showing once the automation has actually started — for an
 * untouched row an all-empty checklist is noise.
 */
const buildSteps = (row) => {
  const decision = String(row.jiraDecision || '').toLowerCase();
  if (decision.includes('ingen avvikelse')) {
    return [];
  }

  const steps = [
    { label: 'Klon skapad', done: Boolean(row.decisionProcessedCloneIssueKey) },
    { label: 'Klon länkad till original', done: Boolean(row.decisionCloneBacklinkCreated) },
  ];

  // The link is mandatory for an approval, so it is a step even before it is done.
  // For 'Underkänd' it is optional and only worth listing once it has happened.
  if (isApproved(row.jiraDecision) || row.decisionReservationLinkCreated) {
    steps.push({ label: 'Avvikelse länkad till AO', done: Boolean(row.decisionReservationLinkCreated) });
  }
  if (decision.includes('godkänd med förbehåll')) {
    steps.push({ label: 'Förbehåll på AO', done: Boolean(row.decisionReservationAdded) });
  }

  return steps;
};

const buildReservationOptions = (row, rows) => {
  const options = new Set();

  if (row.issueKey) {
    options.add(row.issueKey);
  }

  rows
    .filter((candidate) => candidate.testExecutionId === row.testExecutionId && candidate.issueKey)
    .forEach((candidate) => options.add(candidate.issueKey));

  return Array.from(options);
};

const reservationErrorMessage = (value) =>
  (value && !isValidReservationTicketKey(value)
    ? 'Ogiltig Jira-nyckel. Använd formatet INAV-123.'
    : null);

const Chip = ({ tone, children }) => {
  const { fg, bg } = CHIP_TONES[tone] || CHIP_TONES.neutral;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: '999px',
        background: bg,
        color: fg,
        fontSize: '12px',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
};

const Meta = ({ label, children }) => (
  <div style={{ minWidth: '8rem' }}>
    <div
      style={{
        fontSize: '11px',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: 'var(--ids-palette-neutral-60)',
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: '14px', color: 'var(--ids-palette-neutral-30)' }}>{children}</div>
  </div>
);

const Step = ({ done, children }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '12px',
      color: done ? 'var(--ids-palette-success-40)' : 'var(--ids-palette-neutral-60)',
    }}
  >
    <span aria-hidden="true">{done ? '✓' : '○'}</span>
    {children}
  </span>
);

const ReviewerDecisionQueueList = ({
  rows,
  executingDeviationId,
  reservationStateByDeviationId,
  onReservationTicketKeyChange,
  onExecute,
}) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
    {rows.map((row) => {
      const reservationState = reservationStateByDeviationId[row.deviationId] || {};
      const reservationKey = reservationState.reservationTicketKey || '';
      const trimmedKey = reservationKey.trim();
      const reservationOptions = buildReservationOptions(row, rows);
      const optionsListId = `reservation-ticket-options-${row.deviationId}`;
      const showsReservationField = showsReservationTicketKey(row);
      const reservationKeyRequired = requiresReservationTicketKey(row);
      const errorMessage = reservationErrorMessage(trimmedKey);
      const isExecuting = executingDeviationId === row.deviationId;
      const isExecuteDisabled =
        isExecuting || Boolean(errorMessage) || (reservationKeyRequired && !trimmedKey);
      const status = statusChip(row);
      const steps = buildSteps(row);
      const showSteps = steps.some((step) => step.done) || Boolean(row.decisionProcessingError);

      return (
        <article
          key={row.deviationId}
          className="ids-p-4"
          style={{
            border: '1px solid var(--ids-palette-neutral-90)',
            borderLeft: `4px solid ${CHIP_TONES[decisionTone(row.jiraDecision)].edge}`,
            borderRadius: '6px',
            background: 'var(--ids-palette-neutral-100)',
          }}
        >
          <div
            className="ids-flex"
            style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}
          >
            <div>
              <div style={{ fontSize: '18px', fontWeight: 700 }}>
                {row.issueLink ? (
                  <a href={row.issueLink} target="_blank" rel="noreferrer">
                    {row.issueKey}
                  </a>
                ) : (
                  row.issueKey || '—'
                )}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--ids-palette-neutral-50)' }}>
                {[row.organisationName, row.contractName].filter(Boolean).join(' · ') || 'Okänd organisation'}
              </div>
            </div>
            <div className="ids-flex" style={{ gap: '0.5rem', alignItems: 'center' }}>
              <Chip tone={decisionTone(row.jiraDecision)}>{row.jiraDecision || 'Okänt beslut'}</Chip>
              <Chip tone={status.tone}>{status.label}</Chip>
            </div>
          </div>

          <div
            className="ids-flex ids-mt-3"
            style={{ gap: '2rem', flexWrap: 'wrap', rowGap: '0.75rem' }}
          >
            {isApprovedWithReservation(row.jiraDecision) && (
              <Meta label="Förfallodatum">{row.jiraDecisionDuedate || '—'}</Meta>
            )}
            <Meta label="Jira-status">{row.jiraStatus || '—'}</Meta>
            <Meta label="Testomgång">{row.testExecutionId ?? '—'}</Meta>
            <Meta label="Klonat ärende">
              {row.decisionProcessedCloneIssueKey ? (
                row.decisionProcessedCloneIssueLink ? (
                  <a href={row.decisionProcessedCloneIssueLink} target="_blank" rel="noreferrer">
                    {row.decisionProcessedCloneIssueKey}
                  </a>
                ) : (
                  row.decisionProcessedCloneIssueKey
                )
              ) : (
                '—'
              )}
            </Meta>
          </div>

          {showSteps && (
            <div
              className="ids-flex ids-mt-3"
              style={{ gap: '1rem', flexWrap: 'wrap' }}
            >
              {steps.map((step) => (
                <Step key={step.label} done={step.done}>
                  {step.label}
                </Step>
              ))}
            </div>
          )}

          {row.decisionProcessingError && (
            <p
              className="ids-mt-3"
              style={{
                margin: 0,
                padding: '0.5rem 0.75rem',
                borderRadius: '4px',
                background: 'var(--ids-palette-error-95)',
                color: 'var(--ids-palette-error-40)',
                fontSize: '13px',
              }}
            >
              {row.decisionProcessingError}
            </p>
          )}

          <div
            className="ids-flex ids-mt-4"
            style={{
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: '1.5rem',
              flexWrap: 'wrap',
              borderTop: '1px solid var(--ids-palette-neutral-90)',
              paddingTop: '1rem',
            }}
          >
            <div style={{ flex: '1 1 22rem', maxWidth: '32rem' }}>
              {showsReservationField ? (
                <>
                  <IDSInput
                    label={reservationKeyRequired ? 'Avvikelse (t.ex. INAV-310)' : 'Avvikelse (valfri, t.ex. INAV-310)'}
                    list={optionsListId}
                    value={reservationKey}
                    onChange={(e) => onReservationTicketKeyChange(row.deviationId, e.target.value)}
                  />
                  <datalist id={optionsListId}>
                    {reservationOptions.map((key) => (
                      <option key={key} value={key} />
                    ))}
                  </datalist>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '13px', color: 'var(--ids-palette-neutral-60)' }}>
                    Välj en avvikelse från testomgången eller skriv en annan Jira-nyckel.
                    {isApprovedWithReservation(row.jiraDecision) && ' Avvikelsen länkas till anslutningsobjektet och anges i förbehållet.'}
                    {isApproved(row.jiraDecision) && !isApprovedWithReservation(row.jiraDecision) && ' Avvikelsen länkas till anslutningsobjektet.'}
                    {isRejected(row.jiraDecision) && ' Lämna tomt om beslutet inte ska länkas till någon avvikelse.'}
                  </p>
                  {errorMessage && (
                    <p style={{ margin: '0.5rem 0 0', fontSize: '13px', color: 'var(--ids-palette-error-40)' }}>
                      {errorMessage}
                    </p>
                  )}
                </>
              ) : (
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--ids-palette-neutral-60)' }}>
                  {row.decisionProcessed
                    ? 'Beslutet är utfört i Jira.'
                    : 'Beslutet kräver ingen avvikelse.'}
                </p>
              )}
            </div>
            <IDSButton size="s" disabled={isExecuteDisabled} onClick={() => onExecute(row)}>
              {isExecuting ? 'Kör...' : 'Utför beslut'}
            </IDSButton>
          </div>
        </article>
      );
    })}
  </div>
);

export default ReviewerDecisionQueueList;
